/**
 * Builds a playable URL from VideoCenter.VideoUrl values (matches Billtron HostedVideoUrlHelper semantics).
 */

const ALLOWED_EXT = ['.mp4', '.webm', '.m4v', '.mov', '.mpeg', '.mkv', '.avi'];

function tryNormalizeYouTube(uri, host) {
  const h = host || '';
  if (!h.includes('youtube.com') && h !== 'youtu.be') return null;

  let videoId = null;
  const path = uri.pathname || '';

  if (h === 'youtu.be') {
    const seg = path.replace(/^\//, '').split('/')[0];
    if (seg && /^[a-zA-Z0-9_-]{11}$/.test(seg)) videoId = seg;
  } else if (h.includes('youtube.com')) {
    const q = uri.searchParams.get('v');
    if (q) {
      const vRaw = String(q).split('&')[0];
      if (vRaw && /^[a-zA-Z0-9_-]{11}$/.test(vRaw)) videoId = vRaw;
    }
    if (!videoId) {
      const embed = path.match(/\/(?:embed|shorts)\/([a-zA-Z0-9_-]{11})/);
      if (embed) videoId = embed[1];
    }
    if (!videoId) {
      const live = path.match(/^\/live\/([a-zA-Z0-9_-]{11})/);
      if (live) videoId = live[1];
    }
  }

  if (!videoId) return null;
  return `https://www.youtube.com/watch?v=${videoId}`;
}

function extractYouTubeIdFromUrl(playbackUrl) {
  if (!playbackUrl) return null;
  try {
    const uri = new URL(playbackUrl);
    const host = uri.hostname.replace(/\.$/, '').toLowerCase();
    const path = uri.pathname || '';
    if (host === 'youtu.be') {
      const seg = path.replace(/^\//, '').split('/')[0];
      if (seg && /^[a-zA-Z0-9_-]{11}$/.test(seg)) return seg;
    }
    if (host.includes('youtube.com')) {
      const q = uri.searchParams.get('v');
      if (q && /^[a-zA-Z0-9_-]{11}$/.test(q)) return q;
      const embed = path.match(/\/(?:embed|shorts)\/([a-zA-Z0-9_-]{11})/);
      if (embed) return embed[1];
      const live = path.match(/^\/live\/([a-zA-Z0-9_-]{11})/);
      if (live) return live[1];
    }
  } catch {
    return null;
  }
  return null;
}

function extractVimeoIdFromUrl(playbackUrl) {
  if (!playbackUrl) return null;
  try {
    const uri = new URL(playbackUrl);
    const host = uri.hostname.replace(/\.$/, '').toLowerCase();
    if (!host.includes('vimeo.com')) return null;
    const path = uri.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (host === 'player.vimeo.com') {
      const m = path.match(/^video\/(\d+)/);
      return m?.[1] || null;
    }
    const m = path.match(/^(\d+)(?:\/|$)/);
    return m?.[1] || null;
  } catch {
    return null;
  }
}

function tryNormalizeVimeo(uri, host) {
  const h = host || '';
  if (!h.includes('vimeo.com')) return null;

  const path = uri.pathname.replace(/^\//, '').replace(/\/$/, '');
  let m;

  if (h === 'player.vimeo.com') {
    m = path.match(/^video\/(\d+)/);
    if (m && m[1]) return `https://vimeo.com/${m[1]}`;
    return null;
  }

  m = path.match(/^(\d+)(?:\/|$)/);
  if (m && m[1]) return `https://vimeo.com/${m[1]}`;
  return null;
}

function looksLikeDirectHttpsVideo(uri) {
  if (uri.protocol !== 'https:') return false;
  const pathLower = (uri.pathname || '').toLowerCase();
  for (const ext of ALLOWED_EXT) {
    const idx = pathLower.indexOf(ext);
    if (idx < 0) continue;
    const end = idx + ext.length;
    if (pathLower.length === end || pathLower[end] === '?' || pathLower[end] === '#') return true;
  }
  return false;
}

/**
 * @param {string|null|undefined} rawInput Stored VideoCenter.videoUrl / videoUrl field
 * @returns {string|null} URL safe to open in browser / Linking
 */
export function getHostedVideoPlaybackUrl(rawInput) {
  if (rawInput === null || rawInput === undefined) return null;
  const trimmed = String(rawInput).trim();
  if (!trimmed) return null;

  if (/javascript:/i.test(trimmed) || trimmed.toLowerCase().startsWith('data:')) return null;

  if (/^\d{6,}$/.test(trimmed)) {
    return `https://vimeo.com/${trimmed}`;
  }

  let uri;
  try {
    uri = new URL(trimmed);
  } catch {
    return null;
  }

  if (uri.protocol !== 'http:' && uri.protocol !== 'https:') return null;

  if (uri.protocol === 'http:') {
    uri = new URL(trimmed.replace(/^http:/i, 'https:'));
  }

  const host = uri.hostname.replace(/\.$/, '').toLowerCase();

  const yt = tryNormalizeYouTube(uri, host);
  if (yt) return yt;

  const vm = tryNormalizeVimeo(uri, host);
  if (vm) return vm;

  if (looksLikeDirectHttpsVideo(uri)) {
    return uri.href.trim();
  }

  return null;
}

/**
 * @param {string|null|undefined} rawInput Stored VideoCenter.videoUrl / videoUrl field
 * @returns {string|null} Thumbnail URL for supported providers
 */
export function getHostedVideoThumbnailUrl(rawInput) {
  const playbackUrl = getHostedVideoPlaybackUrl(rawInput);
  if (!playbackUrl) return null;

  const youTubeId = extractYouTubeIdFromUrl(playbackUrl);
  if (youTubeId) {
    return `https://img.youtube.com/vi/${youTubeId}/hqdefault.jpg`;
  }

  const vimeoId = extractVimeoIdFromUrl(playbackUrl);
  if (vimeoId) {
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }

  return null;
}
